import _, { toNumber } from 'lodash';
import { useMemo } from 'react';
import { ACCEPTABLE_URL_PARAMS, FilterTypes, INTIAL_FILTERS } from '../../constants/initialFilters';
import {
  ComplexFilterOptions,
  QUERY_FILTERS_OPTIONS,
  RANGE_FILTER_QUERY_OPTIONS,
  SimpleAndRegularFilterOptions,
} from '../../constants/queryFiltersConfig';
import useFilterData from '../Fetch/useGetFiltersFromLS';

type CarlineTypes = {
  [key: string]: string[];
};

const useParseQueryFilters = (): FilterTypes | null => {
  const filterLSData = useFilterData();
  const searchParams = new URLSearchParams(window.location.search);

  const hasFilterParams = ACCEPTABLE_URL_PARAMS.some((param) => searchParams.has(param));
  const isOnlyFilterTypeParam = searchParams.has('type') && !hasFilterParams;

  // Function that will transform filter that have label Complex (carline), because each query
  // value will effect on carline + body type
  const complexFiltersProcessor = (singleFilterData: ComplexFilterOptions, type: string) => {
    const filterQueryValues = searchParams
      .get(type)
      ?.split(',')
      .map((param) => param.toLocaleLowerCase());

    const complexFilterOptions = Object.keys(singleFilterData.options);
    const acceptableFilters = filterQueryValues
      ?.filter((toCheck) => complexFilterOptions.includes(toCheck))
      .map((singleFilter) => singleFilterData.options[singleFilter])
      .reduce((acc, curr) => {
        Object.entries(curr).forEach(([key, value]) => {
          acc[key] = [...(acc[key] || []), ...value];
        });
        return acc;
      }, {});
    if (type === 'carline-group' && acceptableFilters) return acceptableFilters.modelFamily;
    return acceptableFilters;
  };

  // Function that will transform filter that have label Regular - filters that can have multiply
  // values in one array
  const regularFilterProcessor = (
    singleFilterData: SimpleAndRegularFilterOptions,
    type: string,
  ) => {
    const filterQueryValues = searchParams
      .get(type)
      ?.split(',')
      .map((param) => {
        if (param === 'allroad-quattro' || param === 'allroadquattro') return 'allroad-quattro';
        if (param === 'grey') return 'gray';
        return param.toLocaleLowerCase();
      });
    const acceptableFilters = [
      ...new Set(
        filterQueryValues?.filter((toCheck) => singleFilterData.options.includes(toCheck)),
      ),
    ];
    return acceptableFilters;
  };

  // Function that will transform filter that have label Range - filters that can have only 2 values in array
  const rangeFilterProcessor = (type: string) => {
    const filterQueryValues = searchParams
      .get(type)
      ?.split(':')
      .map((param) => parseInt(param, 10));
    if (!filterQueryValues || filterQueryValues.length !== 2) return [];
    const filterOptions = RANGE_FILTER_QUERY_OPTIONS[type];
    const { min, max } = filterOptions.options;
    const minQueryParam = filterQueryValues[0];
    const maxQueryParam = filterQueryValues[1];

    function roundToClosest(value: number, options: number[]) {
      return options.reduce((closest, option) => {
        return Math.abs(option - value) < Math.abs(closest - value) ? option : closest;
      });
    }
    const roundedMin = roundToClosest(minQueryParam, min);
    const roundedMax = roundToClosest(maxQueryParam, max);

    const maxIndex = max.findIndex((option) => option === roundedMax);

    const adjustedMax = () => {
      const maxValueInOptions = max[max.length - 1];
      if (max.includes(maxQueryParam)) return maxQueryParam;
      if (maxQueryParam > maxValueInOptions) return maxValueInOptions;
      return max[maxIndex + 1];
    };

    return [roundedMin, adjustedMax()];
    // return [filterQueryValues[0], filterQueryValues[1]];
  };

  // Function that will transform filter that have label Simple - filters that can have only 1 value and it is string
  const simpleFilterProcessor = (type: string) => {
    const filterQueryValues = searchParams.get(type)?.split(',')[0];
    if (filterQueryValues === 'cpo') return 'used';
    return filterQueryValues;
  };

  // Mapping different filters based on the filter type
  const urlParamGetter = (type: string) => {
    if (!searchParams.get(type) && type !== 'type') return [];

    const singleFilterData = QUERY_FILTERS_OPTIONS[type];

    if (singleFilterData?.filterType === 'complex') {
      const complexFilters = complexFiltersProcessor(
        singleFilterData as ComplexFilterOptions,
        type,
      );
      return complexFilters;
    }
    if (singleFilterData?.filterType === 'regular') {
      const regularFilters = regularFilterProcessor(
        singleFilterData as SimpleAndRegularFilterOptions,
        type,
      );
      return regularFilters;
    }
    if (singleFilterData?.filterType === 'range') {
      const rangeFilters = rangeFilterProcessor(type);
      return rangeFilters;
    }
    if (singleFilterData?.filterType === 'simple') {
      const simpleFilter = simpleFilterProcessor(type);
      return simpleFilter;
    }
    return [];
  };

  // We are grabbing each individual filter from the query params
  // A little bit more lines, but easy to debug in the future.

  const vehicleType = urlParamGetter('type') as string;
  const carlineGroup = urlParamGetter('carline-group') as string[];
  const bodyStyle = urlParamGetter('body-style') as string[];
  const carline = urlParamGetter('carline') as CarlineTypes;
  const price = urlParamGetter('price') as number[];
  const year = urlParamGetter('year') as string[];
  const power = urlParamGetter('power') as number[];
  const gearbox = urlParamGetter('gearbox') as string[];
  const drivetrain = urlParamGetter('drivetrain') as string[];
  const fuelType = urlParamGetter('fuel-type') as string[];
  const wheelSize = urlParamGetter('wheel-size') as string[];
  const exteriorColor = urlParamGetter('colour') as string[];
  const interiorColor = urlParamGetter('interior') as string[];
  const trimline = urlParamGetter('trimline') as string[];
  const mileage = urlParamGetter('mileage') as number[];
  const partnerDealers = urlParamGetter('dealer') as string[];
  // const availability = urlParamGetter('availability');

  const usedInventoryType = useMemo(() => {
    const filterQueryValues = new URLSearchParams(window.location.search)
      .get('type')
      ?.split(',')[0];
    if (filterQueryValues === 'cpo') {
      return 'has-warranties.yes';
    }
    return 'has-warranties.all';
  }, []);

  const hasAnyAcceptedFilters = !![
    ...carlineGroup,
    ...bodyStyle,
    ...Object.keys(carline),
    ...price,
    ...year,
    ...power,
    ...gearbox,
    ...drivetrain,
    ...fuelType,
    ...wheelSize,
    ...exteriorColor,
    ...interiorColor,
    ...trimline,
    ...mileage,
    ...partnerDealers,
  ].length;

  // Preparing all data for the LS

  const combinedFiltersFromQuery = useMemo(() => {
    return {
      vehicleType: vehicleType || 'new',
      usedInventoryType,
      availableInMonths: 0,
      modelFamily: [...new Set([...(carline.modelFamily ?? []), ...carlineGroup])],
      bodyType: [...new Set([...(carline.bodyType ?? []), ...bodyStyle])],
      price: [...price],
      modelYear: [...year],
      mileage: [...mileage],
      power: [...power],
      gearBox: [...gearbox],
      drivetrain: [...drivetrain],
      fuelType: [...new Set([...(carline.fuelType ?? []), ...fuelType])],
      wheelSize: [...wheelSize],
      exteriorColor: [...exteriorColor],
      trimLine: [...trimline],
      interiorColor: [...interiorColor],
      partnerDealers: [...partnerDealers],
      nonAudi: 'foreign-brand.no',
      otherMakes: [],
      nonAudiModels: [],
      preused: [],
    };
  }, [
    vehicleType,
    usedInventoryType,
    carline.modelFamily,
    carline.bodyType,
    carline.fuelType,
    carlineGroup,
    bodyStyle,
    price,
    year,
    mileage,
    power,
    gearbox,
    drivetrain,
    fuelType,
    wheelSize,
    exteriorColor,
    trimline,
    interiorColor,
    partnerDealers,
  ]);

  const listToUpload = useMemo(() => {
    // If we have only vhicle Type in URL params, but at the same time we have LS save
    // we are passing onlt Type filter and data from LS

    if (isOnlyFilterTypeParam && filterLSData) {
      return { ...filterLSData, usedInventoryType, vehicleType };
    }
    // If NO prev LS data and no Other filters (except Type) - we are passing
    // Initial filters + Type filter (if no type - default "new")
    if (!filterLSData && !hasAnyAcceptedFilters) {
      return INTIAL_FILTERS;
    }
    // If no other filters and we HAVE data in LS -  we are passing
    // prev values from LS + type (if no - default value "new")
    if (!hasAnyAcceptedFilters && filterLSData) {
      return filterLSData;
    }

    // In all other cases we are passing what we got from the URL params
    return combinedFiltersFromQuery;
  }, [
    isOnlyFilterTypeParam,
    filterLSData,
    hasAnyAcceptedFilters,
    combinedFiltersFromQuery,
    usedInventoryType,
    vehicleType,
  ]);

  return listToUpload;
};

export default useParseQueryFilters;
